/**
 * WorkerPaymentSetup.js - Payment method configuration for workers
 * Supports Stripe Connect (direct deposit), PayPal, Cash App, Venmo, Zelle
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, Building, DollarSign, Check, X, ExternalLink,
  Smartphone, Mail, AlertTriangle, CheckCircle, RefreshCw,
  ChevronRight, Shield, Banknote, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';

const PAYMENT_METHODS = [
  {
    id: 'stripe_connect',
    name: 'Direct Deposit (Recommended)',
    description: 'Automatic deposits to your bank account via Stripe',
    icon: Building,
    color: 'bg-blue-500',
    badge: 'Fastest',
    requiresSetup: true
  },
  {
    id: 'paypal',
    name: 'PayPal',
    description: 'Receive payments to your PayPal account',
    icon: CreditCard,
    color: 'bg-[#003087]',
    field: 'paypal_email',
    placeholder: 'your@email.com',
    label: 'PayPal Email'
  },
  {
    id: 'cashapp',
    name: 'Cash App',
    description: 'Receive payments via Cash App',
    icon: DollarSign,
    color: 'bg-[#00D632]',
    field: 'cashapp_cashtag',
    placeholder: '$yourcashtag',
    label: 'Cash Tag'
  },
  {
    id: 'venmo',
    name: 'Venmo',
    description: 'Receive payments via Venmo',
    icon: Smartphone,
    color: 'bg-[#008CFF]',
    field: 'venmo_username',
    placeholder: '@yourusername',
    label: 'Venmo Username'
  },
  {
    id: 'zelle',
    name: 'Zelle',
    description: 'Receive payments via Zelle',
    icon: Mail,
    color: 'bg-[#6D1ED4]',
    field: 'zelle_email_or_phone',
    placeholder: 'email or phone',
    label: 'Zelle Email or Phone'
  },
  {
    id: 'check',
    name: 'Paper Check',
    description: 'Receive physical checks mailed to your address',
    icon: Banknote,
    color: 'bg-gray-500',
    requiresAddress: true
  }
];

const WorkerPaymentSetup = ({ workerId, workerName, onClose, onSave }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentSettings, setCurrentSettings] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [formData, setFormData] = useState({});
  
  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [creatingStripeAccount, setCreatingStripeAccount] = useState(false);

  // Fetch current settings
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/workers/${workerId}/payment-details`);
      setCurrentSettings(res.data);
      if (res.data?.preferred_method) {
        setSelectedMethod(res.data.preferred_method);
        setFormData(res.data);
      }
    } catch (error) {
      // May not exist yet
    } finally {
      setLoading(false);
    }
  }, [workerId]);

  // Fetch Stripe Connect status
  const fetchStripeStatus = useCallback(async () => {
    setLoadingStripe(true);
    try {
      const res = await api.get(`/stripe-connect/status/${workerId}`);
      setStripeStatus(res.data);
    } catch (error) {
      setStripeStatus({ has_account: false });
    } finally {
      setLoadingStripe(false);
    }
  }, [workerId]);

  useEffect(() => {
    fetchSettings();
    fetchStripeStatus();
  }, [fetchSettings, fetchStripeStatus]);

  // Create Stripe Connect account
  const createStripeAccount = async () => {
    setCreatingStripeAccount(true);
    try {
      const res = await api.post('/stripe-connect/create-account', {
        worker_id: workerId
      });
      
      if (res.data.already_exists) {
        toast.info('Stripe account already exists. Opening setup...');
      } else {
        toast.success('Stripe account created! Opening bank setup...');
      }
      
      // Get onboarding link and redirect
      const linkRes = await api.post(`/stripe-connect/onboarding-link/${workerId}`);
      window.open(linkRes.data.url, '_blank');
      
      // Refresh status after a delay
      setTimeout(() => fetchStripeStatus(), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Stripe account');
    } finally {
      setCreatingStripeAccount(false);
    }
  };

  // Open Stripe onboarding
  const openStripeOnboarding = async () => {
    setLoadingStripe(true);
    try {
      const res = await api.post(`/stripe-connect/onboarding-link/${workerId}`);
      window.open(res.data.url, '_blank');
      toast.info('Complete setup in the new window, then return here');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open Stripe setup');
    } finally {
      setLoadingStripe(false);
    }
  };

  // Open Stripe Dashboard
  const openStripeDashboard = async () => {
    try {
      const res = await api.get(`/stripe-connect/dashboard-link/${workerId}`);
      window.open(res.data.url, '_blank');
    } catch (error) {
      toast.error('Stripe Dashboard not available. Please complete setup first.');
    }
  };

  // Save payment settings
  const saveSettings = async () => {
    if (!selectedMethod) {
      toast.error('Please select a payment method');
      return;
    }

    // Validate required fields
    const method = PAYMENT_METHODS.find(m => m.id === selectedMethod);
    if (method?.field && !formData[method.field]) {
      toast.error(`Please enter your ${method.label}`);
      return;
    }

    if (selectedMethod === 'stripe_connect' && !stripeStatus?.payouts_enabled) {
      toast.error('Please complete Stripe setup first');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/workers/${workerId}/payment-details`, {
        preferred_method: selectedMethod,
        ...formData
      });
      
      toast.success('Payment settings saved!');
      if (onSave) onSave();
      if (onClose) onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const renderStripeSetup = () => {
    if (loadingStripe) {
      return (
        <div className="p-4 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
        </div>
      );
    }

    if (!stripeStatus?.has_account) {
      return (
        <div className="p-4 space-y-4">
          <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">Secure Bank Connection</p>
              <p className="text-sm text-blue-600">
                Connect your bank account securely through Stripe. Your banking info is never stored on our servers.
              </p>
            </div>
          </div>
          
          <Button
            onClick={createStripeAccount}
            disabled={creatingStripeAccount}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            {creatingStripeAccount ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Setting up...
              </>
            ) : (
              <>
                <Building className="w-4 h-4 mr-2" />
                Set Up Direct Deposit
              </>
            )}
          </Button>
        </div>
      );
    }

    if (!stripeStatus.payouts_enabled) {
      return (
        <div className="p-4 space-y-4">
          <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Setup Incomplete</p>
              <p className="text-sm text-amber-600">
                Please complete your bank account setup to receive direct deposits.
              </p>
              {stripeStatus.currently_due?.length > 0 && (
                <p className="text-xs text-amber-500 mt-1">
                  Still needed: {stripeStatus.currently_due.join(', ')}
                </p>
              )}
            </div>
          </div>
          
          <Button
            onClick={openStripeOnboarding}
            disabled={loadingStripe}
            className="w-full"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Continue Setup
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchStripeStatus}
            className="w-full text-[var(--text-muted)]"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Status
          </Button>
        </div>
      );
    }

    return (
      <div className="p-4 space-y-4">
        <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          <div>
            <p className="font-medium text-green-800">Direct Deposit Ready!</p>
            <p className="text-sm text-green-600">
              Your bank account is connected. Payroll will be deposited automatically.
            </p>
          </div>
        </div>
        
        <Button
          variant="outline"
          onClick={openStripeDashboard}
          className="w-full"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Manage Bank Account
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-h-[80vh] overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-[var(--background-paper)] p-4 border-b z-10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Payment Setup</h2>
            <p className="text-sm text-[var(--text-muted)]">
              Choose how you want to receive your payroll
            </p>
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Current Status */}
      {currentSettings?.preferred_method && (
        <div className="p-4 bg-[var(--background-subtle)] border-b">
          <p className="text-sm text-[var(--text-muted)]">Current method:</p>
          <p className="font-semibold capitalize">
            {PAYMENT_METHODS.find(m => m.id === currentSettings.preferred_method)?.name || currentSettings.preferred_method}
          </p>
        </div>
      )}

      {/* Payment Methods */}
      <div className="p-4 space-y-3">
        {PAYMENT_METHODS.map(method => {
          const isSelected = selectedMethod === method.id;
          const Icon = method.icon;
          
          return (
            <div key={method.id}>
              <button
                onClick={() => setSelectedMethod(method.id)}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  isSelected 
                    ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                    : 'border-[var(--border-subtle)] hover:border-[#D4AF37]/50'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${method.color} text-white`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold">{method.name}</p>
                      {method.badge && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          {method.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[var(--text-muted)]">{method.description}</p>
                  </div>
                  <ChevronRight className={`w-5 h-5 transition-transform ${isSelected ? 'rotate-90' : ''}`} />
                </div>
              </button>

              {/* Expanded form */}
              <AnimatePresence>
                {isSelected && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="border-2 border-t-0 border-[#D4AF37] rounded-b-xl">
                      {method.id === 'stripe_connect' ? (
                        renderStripeSetup()
                      ) : method.field ? (
                        <div className="p-4">
                          <label className="block text-sm font-medium mb-2">{method.label}</label>
                          <Input
                            type={method.field === 'paypal_email' ? 'email' : 'text'}
                            placeholder={method.placeholder}
                            value={formData[method.field] || ''}
                            onChange={(e) => setFormData({...formData, [method.field]: e.target.value})}
                          />
                        </div>
                      ) : method.requiresAddress ? (
                        <div className="p-4">
                          <p className="text-sm text-[var(--text-muted)] mb-3">
                            Checks will be mailed to your address on file. Contact the owner to update your mailing address.
                          </p>
                          <div className="flex items-center gap-2 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-sm">Using your profile address</span>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Save Button */}
      <div className="sticky bottom-0 bg-[var(--background-paper)] p-4 border-t">
        <Button
          onClick={saveSettings}
          disabled={saving || !selectedMethod || (selectedMethod === 'stripe_connect' && !stripeStatus?.payouts_enabled)}
          className="w-full btn-primary"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Save Payment Method
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default WorkerPaymentSetup;
